/**
 * Design System Tokens — единый источник истины для всех цветов, радиусов и теней.
 *
 * Используется в:
 * - ThemeManager.tsx (MUI createTheme)
 * - Storybook docs (визуальная документация)
 * - Компоненты (через theme.palette или прямой импорт для edge-cases)
 */

/**
 * Ядро дизайн-системы приходит из `@aspid/ui` (AF-23): семантические цвета,
 * шкала теней и радиусы — 18 значений, одинаковых во всех трёх приложениях.
 * Здесь остаётся только своё. Импорты по приложению не менялись.
 */
import { colorSchemes, shadows as coreShadows, radii } from '@aspid/ui';

// ============================================================
// PALETTE
// ============================================================

/**
 * `palette` — единственный источник значений (AF-43).
 *
 * До этого значения были поделены между `palette` (семантика) и `lightPalette`
 * (поверхности), и у токена было два возможных адреса: чтобы узнать, где живёт
 * `border.default`, надо было помнить соглашение. У deals такого деления нет —
 * там `palette` полный источник, а `lightPalette` его проекция, — и владелец
 * выбрал эту форму для всех трёх.
 *
 * Довод, решивший выбор: `colorSchemes.light` из `@aspid/ui` вливается сюда
 * через spread ниже. Пока поверхности лежали в отдельном объекте, они этим
 * путём не питались, и подъём их в пакет потребовал бы второй, другой дороги.
 * Теперь семантика и поверхности стоят в одной позиции.
 */
export const palette = {
  ...colorSchemes.light,
  secondary: {
    /**
    Monochrome: нейтральный серый вместо розового
    */
    main: '#757575',
    light: '#F5F5F5',
  },
  /**
  Голубой акцент (лого ФРИИ, третичный цвет)
  */
  cyan: '#3AACE6',
  background: {
    /**
    Monochrome: нейтральный серый фон
    */
    default: '#FAFAFA',
    paper: '#FFFFFF',
  },
  text: {
    /**
    Monochrome: чистый тёмный, без синего оттенка
    */
    primary: '#1A1A1A',
    secondary: '#555555',
    disabled: '#888888',
    /*
     * `muted` — приглушённый, но ЧИТАЕМЫЙ текст (AF-67).
     *
     * Появился потому, что роль «третий уровень приглушения» существовала и
     * раньше, только выражалась токеном `disabled`: по замеру 142 вхождения
     * красили им содержимое, которое пользователь обязан прочесть.
     *
     *   disabled  #888888  3.54:1   ниже порога AA 4.5 для текста
     *   muted     #757575  4.61:1   проходит, и заметно светлее secondary
     *   secondary #555555  7.46:1
     *
     * Почему отдельный токен, а не `secondary`. В AF-65 третий токен был
     * отвергнут — но там речь шла о трёх ссылках, которым `secondary`
     * подходил по смыслу. Здесь замер другой: в 81 файле из 122 `secondary` и
     * `disabled` встречаются ВМЕСТЕ, то есть два уровня приглушения заданы
     * намеренно. Свести их значило бы убрать уровень оформления ради порога,
     * а не привести оформление в соответствие с порогом.
     *
     * `disabled` остаётся `#888888` для настоящей неактивности (12 вхождений):
     * там приглушённость и есть смысл, и WCAG 1.4.3 такие элементы исключает.
     */
    muted: '#757575',
    /**
    Подсказки, placeholder
    */

  },
  border: {
    /**
    Monochrome: нейтральные серые границы
    */
    default: '#E0E0E0',
    light: '#F0F0F0',
    focus: '#BDBDBD',
  },
} as const;

// ============================================================
// LIGHT MODE
// ============================================================

/**
 * Проекция `palette`, а не второй набор значений.
 *
 * Форма сохранена ровно та, что была: те же ключи, те же значения — поэтому ни
 * один из потребителей не правится. Литералов здесь нет намеренно: появившийся
 * тут литерал означает значение, которого нет в `palette`, то есть возврат к
 * двум адресам у одного токена.
 */
export const lightPalette = {
  background: palette.background,
  text: palette.text,
  border: palette.border,
  divider: palette.border.default,
} as const;

// ============================================================
// DARK MODE — переопределения поверх MUI dark defaults
// ============================================================

export const darkPalette = {
  divider: 'rgba(255, 255, 255, 0.12)',
  /**
   * Тёмный аналог `text` — ровно один ключ, и только потому, что MUI своего
   * не знает (AF-575/AF-576).
   *
   * `primary`, `secondary` и `disabled` в тёмной теме выводит сам MUI, и они
   * здесь намеренно не переопределяются. `muted` — наш токен (AF-67), MUI о
   * нём не слышал, поэтому в тёмной теме он оставался `undefined`: `color:
   * 'text.muted'` не резолвился, CSS значение отбрасывал, элемент наследовал
   * цвет родителя. Молча — ни предупреждения, ни падения.
   *
   * ─── Почему 0.6 ────────────────────────────────────────────────────────
   *
   * Значение продолжает лестницу MUI, а не выбрано на глаз. Решение владельца
   * 27.09.2026: «чинить в соответствии с MUI». Контраст посчитан на дефолтном
   * тёмном фоне `#121212`:
   *
   *     secondary  rgba(255, 255, 255, 0.7)  #B8B8B8   9.44:1   дефолт MUI
   *     muted      rgba(255, 255, 255, 0.6)  #A0A0A0   7.16:1   этот токен
   *     disabled   rgba(255, 255, 255, 0.5)  #888888   5.28:1   дефолт MUI
   *
   * Порядок приглушения тот же, что в светлой теме (secondary → muted →
   * disabled), и порог AA 4.5 проходит с запасом — как и `#757575` в светлой
   * (4.61:1), ради чего токен и заводился.
   *
   * ─── Отвергнуто: взять светлое `#757575` ───────────────────────────────
   *
   * На `#121212` оно даёт 4.02:1 — ниже AA. Токен, заведённый ради порога, не
   * может его нарушать во втором режиме.
   *
   * ─── Отвергнуто: спред `...lightPalette` в обе ветки, как у deals ──────
   *
   * Так `muted` появился бы, но вместе с ним в тёмную тему уехали бы СВЕТЛЫЕ
   * цвета текста: у deals `createAppTheme('dark').palette.text.primary` равен
   * `#1A1A1A` — почти чёрный на тёмном фоне. Это дефект deals, а не образец.
   */
  text: {
    muted: 'rgba(255, 255, 255, 0.6)',
  },
} as const;

// ============================================================
// TYPOGRAPHY
// ============================================================

export const typography = {
  fontFamily: [
    '"Inter"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'sans-serif',
  ].join(','),
  /**
  Моноширинный шрифт для числовых значений, score-pills, code
  */
  monoFontFamily: ['"JetBrains Mono"', '"DM Mono"', 'monospace'].join(','),
  fontSize: 14,
  headings: {
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  /**
  Section labels: uppercase мини-заголовки (Swiss-стиль)
  */
  sectionLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  },
} as const;

// ============================================================
// SHAPE & RADIUS
// ============================================================

export const shape = {
  ...radii,
  /**
  Карточки, Paper, Dialog — Swiss-Monochrome стиль
  */
  /**
  Кнопки
  */
  /**
  Инпуты
  */
  /**
  Маленькие элементы (chips, badges, pills)
  */
  smallRadius: 2,
  /**
  Landing: полностью sharp (Swiss Modernism)
  */
  landingRadius: 0,
} as const;

// ============================================================
// SHADOWS
// ============================================================

export const shadows = {
  ...coreShadows,
  /**
  Swiss-стиль: минимальные тени, предпочтение border
  */
  lg: '0 4px 16px rgba(0, 0, 0, 0.06)',
  /**
  Focus ring — navy accent
  */
  /**
  Focus ring для ошибок
  */
  errorFocusRing: '0 0 0 3px rgba(154, 44, 44, 0.1)',
} as const;

// ============================================================
// BORDERS — Swiss-стиль: border-first вместо shadow-first
// ============================================================

export const borders = {
  /**
  Основной border для карточек, Paper
  */
  default: `1px solid ${lightPalette.border.default}`,
  /**
  Усиленный border для акцентных элементов
  */
  strong: `1px solid ${lightPalette.text.primary}`,
  /**
  Тонкий разделитель
  */
  light: `1px solid ${lightPalette.border.light}`,
} as const;

// ============================================================
// TRANSITIONS
// ============================================================

export const transitions = {
  default: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// ============================================================
// LANDING PAGE — специфичные токены для секций с фиксированным фоном
// ============================================================

export const landing = {
  hero: {
    /**
    Swiss Modernism: белый фон, чёрный текст — из demo-2
    */
    gradient: lightPalette.background.paper,
    textColor: lightPalette.text.primary,
    mutedTextColor: lightPalette.text.secondary,
    /*
     * Тоже читаемый текст, не неактивный (AF-65) — см. довод у `footer`.
     */
    subtleTextColor: lightPalette.text.secondary,
    /**
    Акцентный цвет для выделенных слов — основной navy
    */
    accentColor: palette.primary.main,
  },
  footer: {
    /**
    Swiss: минимальный footer, border-top
    */
    background: lightPalette.background.paper,
    /*
     * `text.secondary`, а не `text.disabled` (AF-65).
     *
     * Здесь роль — читаемый приглушённый текст и ССЫЛКИ, а не неактивность.
     * `text.disabled` (#888888) даёт на белом 3.54:1, ниже порога AA 4.5 для
     * текста. Для ссылки исключение WCAG 1.4.3 про неактивные элементы не
     * применяется: она интерактивна.
     *
     *   text.disabled   #888888   3.54:1   не проходит
     *   text.secondary  #555555   7.46:1   проходит
     *
     * Значение `text.disabled` при этом НЕ поднято: у него есть своя законная
     * роль — по замеру 12 вхождений вида `&.Mui-disabled` и `<MenuItem disabled>`,
     * где приглушённость и есть смысл. Поднять его значило бы сделать
     * неактивные элементы неотличимыми от активных.
     */
    textColor: lightPalette.text.secondary,
    linkColor: lightPalette.text.secondary,
    linkHoverColor: lightPalette.text.primary,
    borderTop: `1px solid ${lightPalette.text.primary}`,
  },
  header: {
    /**
    Swiss: белый фон, border-bottom
    */
    transparentBg: 'transparent',
    solidBg: '#FFFFFF',
    borderBottom: `1px solid ${lightPalette.text.primary}`,
    textColor: lightPalette.text.primary,
    textColorInverted: lightPalette.text.primary,
    separatorColor: lightPalette.border.default,
    hoverBg: `rgba(0, 0, 0, 0.04)`,
  },
  features: {
    accentColors: {
      navy: '#3A558C',
      pink: '#F291A3',
      cyan: '#3AACE6',
      /**
      Amber/yellow accent — аномалии, FinTech
      */
      amber: '#F59E0B',
      amberLight: '#FBBF24',
      amberDark: '#D97706',
      /**
      Green accent — калькуляторы, EdTech, бенчмарки
      */
      green: '#22C55E',
      greenLight: '#4ADE80',
      /**
      Violet accent — самопроверка, согласованность
      */
      violet: '#8B5CF6',
      /**
      Primary light blend — градиенты, декоративные bullet
      */
      primaryLight: '#7BA3DB',
    },
  },
  /**
  Swiss: строгие разделители
  */
  divider: {
    strong: `1px solid ${lightPalette.text.primary}`,
    light: `1px solid ${lightPalette.border.default}`,
  },
} as const;
